import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"

// Async searchable multi-user picker for the calendar filter.
// Fetches matching company users from the server as the user types (debounced).
// Selected users are shown as removable tags; the form submits their IDs as user_ids[].
export default class extends Controller {
  static targets = ["input", "dropdown", "results", "tags", "empty", "spinner"]
  static values  = { searchUrl: String, selected: Array }

  connect() {
    this._debounceTimer = null
    this._boundClose    = this._closeOnOutsideClick.bind(this)
    document.addEventListener("click", this._boundClose)
  }

  disconnect() {
    document.removeEventListener("click", this._boundClose)
    clearTimeout(this._debounceTimer)
  }

  // Toggle the dropdown open/closed when the trigger button is clicked.
  toggle(event) {
    event.stopPropagation()
    const isHidden = this.dropdownTarget.classList.contains("hidden")
    if (isHidden) {
      this.dropdownTarget.classList.remove("hidden")
      this.inputTarget.focus()
      if (this.inputTarget.value.trim() === "") this._search("")
    } else {
      this._close()
    }
  }

  // Handle typing in the search box.
  search() {
    clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => {
      this._search(this.inputTarget.value.trim())
    }, 200)
  }

  // Select a user from the results list.
  select(event) {
    event.stopPropagation()
    const el   = event.currentTarget
    const id   = parseInt(el.dataset.userId, 10)
    const name = el.dataset.userName

    if (!this.selectedValue.find(u => u.id === id)) {
      this.selectedValue = [...this.selectedValue, { id, name }]
      this._renderTags()
      this._renderHiddenInputs()
    }
    // Re-render results to mark the item checked
    this._renderResult(el, true)
  }

  // Remove a tag (deselect a user).
  remove(event) {
    event.preventDefault()
    event.stopPropagation()
    const id = parseInt(event.currentTarget.dataset.userId, 10)
    this.selectedValue = this.selectedValue.filter(u => u.id !== id)
    this._renderTags()
    this._renderHiddenInputs()
    // Re-render that result row as unchecked if visible
    const row = this.resultsTarget.querySelector(`[data-user-id="${id}"]`)
    if (row) this._renderResult(row, false)
  }

  // ── private ──────────────────────────────────────────────────────────────

  async _search(query) {
    this._setLoading(true)
    try {
      const url = new URL(this.searchUrlValue, window.location.origin)
      url.searchParams.set("q", query)
      const resp  = await fetch(url, { headers: { Accept: "application/json" } })
      const users = await resp.json()
      this._renderResults(users)
    } catch (_) {
      // network error – show nothing
    } finally {
      this._setLoading(false)
    }
  }

  _renderResults(users) {
    this.resultsTarget.innerHTML = ""
    if (users.length === 0) {
      this.resultsTarget.innerHTML =
        `<p class="px-3 py-3 text-sm text-gray-500">${t("javascript.controllers.calendar_user_filter_controller.no_users_found")}</p>`
      return
    }
    users.forEach(u => {
      const checked = !!this.selectedValue.find(s => s.id === u.id)
      const div = document.createElement("div")
      div.dataset.userId   = u.id
      div.dataset.userName = u.name
      div.dataset.action   = "click->calendar-user-filter#select"
      div.className        = "flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 cursor-pointer"
      div.innerHTML = `
        <input type="checkbox" tabindex="-1" ${checked ? "checked" : ""}
               class="rounded border-gray-300 text-primary focus:ring-primary h-4 w-4 shrink-0 pointer-events-none">
        <div class="min-w-0">
          <div class="text-sm text-gray-800 truncate">${this._esc(u.name)}</div>
          <div class="text-xs text-gray-500 truncate">${this._esc(u.email)}</div>
        </div>`
      this.resultsTarget.appendChild(div)
    })
  }

  _renderResult(el, checked) {
    const cb = el.querySelector("input[type=checkbox]")
    if (cb) cb.checked = checked
  }

  _renderTags() {
    this.tagsTarget.innerHTML = ""
    this.selectedValue.forEach(u => {
      const tag = document.createElement("span")
      tag.className = "inline-flex items-center gap-1 pl-2.5 pr-1.5 py-0.5 rounded-full bg-primary/10 border border-primary/30 text-primary text-xs font-medium"
      tag.innerHTML = `
        <span class="truncate max-w-[120px]">${this._esc(u.name)}</span>
        <button type="button"
                data-action="click->calendar-user-filter#remove"
                data-user-id="${u.id}"
                class="ml-0.5 rounded-full hover:bg-primary/20 p-0.5 transition-colors"
                aria-label="${this._esc(t("javascript.controllers.calendar_user_filter_controller.remove_user", { name: u.name }))}">
          <svg class="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M18 6 6 18"></path>
            <path d="m6 6 12 12"></path>
          </svg>
        </button>`
      this.tagsTarget.appendChild(tag)
    })
  }

  _renderHiddenInputs() {
    // Remove old hidden inputs for user_ids
    this.element.querySelectorAll("input[data-cal-user-hidden]").forEach(el => el.remove())
    this.selectedValue.forEach(u => {
      const inp = document.createElement("input")
      inp.type  = "hidden"
      inp.name  = "user_ids[]"
      inp.value = u.id
      inp.dataset.calUserHidden = "1"
      this.element.appendChild(inp)
    })
  }

  _setLoading(on) {
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.toggle("hidden", !on)
  }

  _close() {
    this.dropdownTarget.classList.add("hidden")
    this.inputTarget.value = ""
    this.resultsTarget.innerHTML = ""
  }

  _closeOnOutsideClick(event) {
    if (!this.element.contains(event.target)) this._close()
  }

  _esc(str) {
    return String(str)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
